import { UserCategory } from '../../../../../map/user-guide/user-guide.service';

export interface CategoryChip {
  id: UserCategory;
  label: string;
}

export const CATEGORY_CHIPS: CategoryChip[] = [
  { id: 'homebuyer', label: 'Homebuyer' },
  { id: 'realtor', label: 'Realtor' },
  { id: 'business', label: 'Business' },
  { id: 'researcher', label: 'Academia' },
  { id: 'healthcare', label: 'Healthcare' },
  { id: 'other', label: 'Other' },
];

// Annual income growth is highlighted in the guide — every preset must include it.
const INCOME_GROWTH = 'MEDIAN_HOUSEHOLD_INCOME_TREND';

const HOMEBUYER_FEATURES = new Set([
  // Population
  'population', 'MEDIAN_AGE', 'AVERAGE_HOUSEHOLD_SIZE',
  'AGE_SEX_DISTRIBUTION_CHART', 'RACE_DISTRIBUTION_CHART',
  'POPULATION_BY_YEARS', 'MARITAL_STATUS_CHART',
  // Income & Employment
  'MEDIAN_INCOME', INCOME_GROWTH, 'INCOME_DISTRIBUTION_CHART',
  // Health & Environment
  'LIFE_EXPECTANCY', 'PRIMARY_CARE_PHYSICIANS_RATIO',
  'UNHEALTHY_BEHAVIOUR_INDEX', 'SMOKERS_FRACTION', 'DRINKERS_FRACTION',
  'AIR_POLLUTION_INDEX',
  // Education
  'BACHELORS_DEGREE_OR_HIGHER_FRACTION', 'EDUCATION_ATTAINMENT_CHART', 'SCHOOLS_POI',
  // Housing
  'MEDIAN_HOUSING_SALE_COST_TREND', 'MEDIAN_GROSS_RENT_TREND',
  'MEDIAN_GROSS_RENT', 'MEDIAN_HOUSING_SALE_COST',
  // Crime
  'CRIME_RATE', 'VIOLENT_CRIME_RATE', 'PROPERTY_CRIME_RATE',
  // Commute & mobility
  'AVERAGE_TRAVEL_TIME_TO_WORK', 'WORK_AT_HOME',
  'CUMULATIVE_TRAFFIC_INTENSITY', 'road_density_per_mi2',
  'TIME_TO_DEPARTURE_TO_WORK_CHART',
  // Area Summary
  'HEALTH_INDEX', 'WEALTH_INDEX', 'WISE_INDEX', 'HWW_INDEX',
  // Places
  'FAMILY_POI_CHART', 'RETIREMENT_POI_CHART', 'CONVENIENCE_POI_CHART',
  'HEALTHCARE_POI_CHART', 'BEAUTY_POI_CHART', 'OUTDOOR_POI_CHART',
]);

const REALTOR_FEATURES = new Set([
  ...HOMEBUYER_FEATURES,
  // Population extras
  'POPULATION_GROWTH_TREND', 'HOUSEHOLDS',
  // Income extras
  'GINI_INDEX', 'UNEMPLOYMENT_RATIO', 'UNEMPLOYMENT_RATE_BY_YEARS_CHART',
  // Health extras
  'UNINSURED_RATIO', 'DENTISTS_RATIO', 'MENTAL_HEALTH_PROVIDERS_RATIO',
  'SPECIAL_HEALTH_CARE_NEEDS_INDEX', 'AIR_POLLUTION', 'AIR_QUALITY_DISTRIBUTION_CHART',
  // Housing extras
  'MEDIAN_TENURE', 'HOME_OWNERSHIP_RATIO', 'HOME_PRICE_TO_INCOME_RATIO',
  'VACANCY_CHART', 'GROSS_RENT_DISTRIBUTION_CHART',
  'HOUSING_SELL_PRICE_DISTRIBUTION_CHART', 'MEDIAN_NUMBER_OF_ROOMS',
  'NUMBER_OF_ROOMS_CHART', 'TENURE_CHART',
  // Crime extras
  'INDIVIDUAL_CRIMES', 'BUSINESS_CRIMES',
  // Commute extras
  'NUMBER_OF_VEHICLES_AVAILABLE',
  // Places extras
  'COMPETITORS_CHART', 'FINANCIAL_SERVICES_POI_CHART',
]);

const HEALTHCARE_FEATURES = new Set([
  // Health & Environment (primary focus)
  'LIFE_EXPECTANCY', 'UNINSURED_RATIO',
  'PRIMARY_CARE_PHYSICIANS_RATIO', 'DENTISTS_RATIO', 'MENTAL_HEALTH_PROVIDERS_RATIO',
  'UNHEALTHY_BEHAVIOUR_INDEX', 'SPECIAL_HEALTH_CARE_NEEDS_INDEX',
  'SMOKERS_FRACTION', 'DRINKERS_FRACTION', 'OBESITY_FRACTION',
  'DIABETES_FRACTION', 'CANCER_FRACTION', 'HEART_DISEASES_FRACTION',
  'INSURANCE_DISTRIBUTION_CHART',
  'AIR_POLLUTION_INDEX', 'AIR_POLLUTION', 'AIR_QUALITY_DISTRIBUTION_CHART',
  // Population
  'population', 'MEDIAN_AGE',
  'AGE_SEX_DISTRIBUTION_CHART', 'RACE_DISTRIBUTION_CHART',
  // Income (basic + always-included growth)
  'MEDIAN_INCOME', INCOME_GROWTH, 'INCOME_DISTRIBUTION_CHART',
  // Places
  'HEALTHCARE_POI_CHART',
]);

const BUSINESS_FEATURES = new Set([
  // Population
  'population', 'POPULATION_GROWTH_TREND', 'HOUSEHOLDS',
  'AGE_SEX_DISTRIBUTION_CHART', 'RACE_DISTRIBUTION_CHART', 'POPULATION_BY_YEARS',
  // Income & Employment
  'MEDIAN_INCOME', INCOME_GROWTH,
  'GINI_INDEX', 'AVERAGE_ANNUAL_SPENDING', 'UNEMPLOYMENT_RATIO',
  'INCOME_DISTRIBUTION_CHART',
  // Health (full — covers healthcare/drugstore niche)
  ...HEALTHCARE_FEATURES,
  // Education
  'BACHELORS_DEGREE_OR_HIGHER_FRACTION', 'EDUCATION_ATTAINMENT_CHART',
  // Crime
  'CRIME_RATE', 'BUSINESS_CRIMES', 'VIOLENT_CRIME_RATE', 'PROPERTY_CRIME_RATE',
  // Commute & mobility
  'NUMBER_OF_VEHICLES_AVAILABLE', 'AVERAGE_TRAVEL_TIME_TO_WORK',
  'WORK_AT_HOME', 'CUMULATIVE_TRAFFIC_INTENSITY', 'road_density_per_mi2',
  'HOUSEHOLDS_BY_VEHICLES_CHART', 'TIME_TO_DEPARTURE_TO_WORK_CHART',
  // Target audience
  'CONVENIENCE_STORES', 'DRUGSTORES', 'BEAUTY', 'RETAIL', 'TARGET_AUDIENCE_CHART',
  // Places
  'COMPETITORS_CHART', 'FAMILY_POI_CHART', 'RETIREMENT_POI_CHART',
  'CONVENIENCE_POI_CHART', 'HEALTHCARE_POI_CHART', 'BEAUTY_POI_CHART',
  'FINANCIAL_SERVICES_POI_CHART', 'OUTDOOR_POI_CHART',
]);

// null = show everything (no filtering)
export const PRESET_FEATURES: Record<UserCategory, Set<string> | null> = {
  homebuyer: HOMEBUYER_FEATURES,
  realtor: REALTOR_FEATURES,
  business: BUSINESS_FEATURES,
  healthcare: HEALTHCARE_FEATURES,
  researcher: null,
  other: null,
  unspecified: null,
};
